import { Container, LoadingScreen } from '@/shared/components'
import { useGetAllImporters } from '@/features/importers/hooks/importers'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import { BreadcrumbsBar, Button, DataBlock, H2, PageSection } from '@/common'
import useDialog from '@/common/hooks/useDialog'
import { compact } from 'es-toolkit'
import { useMemo } from 'react'
import { Navigate, useParams, useNavigate } from 'react-router-dom'
import ImporterEditDialog from './dialogs/ImporterEditDialog'
import DeleteImporterDialog from '../../dialogs/DeleteImporterDialog'

export default function ImporterDetailsPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const {
    open: isEditImporterDialogOpen,
    openDialog: openEditImporterDialog,
    closeDialog: closeEditImporterDialog
  } = useDialog()
  const {
    open: isDeleteImporterDialogOpen,
    openDialog: openDeleteImporterDialog,
    closeDialog: closeDeleteImporterDialog
  } = useDialog()

  const organizationId = useSession().session.organization.id
  const importerId = useParams().importerId!

  const importersQuery = useGetAllImporters(organizationId)

  const areImportersLoading = importersQuery.isLoading

  const importers = importersQuery.data ?? []
  const importer = importers.find(importer => importer.id === importerId)

  const importerAddress = useMemo(() => {
    const streetAndNumber =
      importer?.street || importer?.streetNumber
        ? compact([importer?.street, importer?.streetNumber]).join(' ')
        : ''

    const address = compact([
      streetAndNumber,
      importer?.postCode,
      importer?.city,
      t(`common:countriesByIsoAlpha2.${importer?.country}`)
    ]).join(', ')
    return address
  }, [importer, t])

  if (areImportersLoading) {
    return <LoadingScreen />
  }

  if (!importer || !importerId) {
    return <Navigate to="/importers" />
  }

  const breadcrumbs = [
    { label: t('common:breadcrumbs.importers'), to: '/importers' },
    { label: importer?.name || '', to: '' }
  ]

  return (
    <>
      <BreadcrumbsBar links={breadcrumbs} />
      <Container>
        <header className="flex justify-between items-center">
          <H2>{importer?.name}</H2>
        </header>
        <PageSection
          title={t('importers:importerViewPage.importerDetails.title')}
          actions={
            <div className="flex gap-2">
              <Button variant="txt-default" onClick={openEditImporterDialog}>
                {t('importers:importerViewPage.importerDetails.actions.edit')}
              </Button>
              <Button variant="danger" onClick={openDeleteImporterDialog}>
                {t('common:actions.delete')}
              </Button>
            </div>
          }
        >
          <div className="grid sm:grid-cols-3 gap-4">
            <DataBlock
              label={t(
                'importers:importerViewPage.importerDetails.dataSections.identificationNumber'
              )}
              data={importer?.identificationNumber}
            />
            <DataBlock
              label={t(
                'importers:importerViewPage.importerDetails.dataSections.customId'
              )}
              data={importer?.customId || '-'}
            />
            <DataBlock
              label={t('importers:importerViewPage.importerDetails.dataSections.address')}
              data={importerAddress}
            />
          </div>
        </PageSection>
      </Container>
      <ImporterEditDialog
        isOpen={isEditImporterDialogOpen}
        close={closeEditImporterDialog}
        importer={importer || null}
        organizationId={organizationId}
      />
      <DeleteImporterDialog
        isOpen={isDeleteImporterDialogOpen}
        close={closeDeleteImporterDialog}
        importerName={importer.name}
        organizationId={organizationId}
        importerId={importerId}
        onAfterLeave={() => navigate('/importers')}
      />
    </>
  )
}
